import {
  LitElement,
  html,
  css,
  styleMap,
  classMap,
  styles,
} from '@hh/global.js'

class CollectionPagination extends LitElement {
  static properties = {
    loading: {
      reflect: true,
      type: Boolean,
    },
    totalItems: {
      attribute: 'total-items',
      type: Number,
    },
    totalPages: {
      attribute: 'total-pages',
      type: Number,
    },
    currentParamsPage: {
      attribute: 'current-params-page',
      type: Number,
    },
    perPage: {
      attribute: 'per-page',
      type: Number,
    },
    listSelector: {
      attribute: 'list-selector',
      type: String,
    },
    cardSelector: {
      attribute: 'card-selector',
      type: String,
    },
    tVerb: {
      attribute: 't-verb',
      type: String,
    },
    tSeparator: {
      attribute: 't-separator',
      type: String,
    },
    tAria: {
      attribute: 't-aria',
      type: String,
    },
    _currentPage: {
      state: true,
      type: Number,
    },
    _isLastPage: {
      state: true,
    },
  }

  constructor() {
    super()
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  }

  connectedCallback() {
    super.connectedCallback()
    this._currentPage = this.currentParamsPage
    this.loading = false
    this.updateComplete.then(() => {
      this._checkLastPage()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  updated(changedProperties) {
    if (changedProperties.has('_currentPage')) {
      this._checkLastPage()
    }
    if (changedProperties.has('loading')) {
      this.toggleButtonLoading()
    }
  }

  async onLoadMore(e) {
    e.preventDefault()
    this.loading = true
    this.dispatchEvent(new CustomEvent('load-more'))
    try {
      const nextPage = await this._fetchPage(this._currentPage + 1)
      this._appendProducts(nextPage)
      this._currentPage++
    } catch (error) {
      console.error(error)
      this.loading = false
    } finally {
      this.loading = false
    }
  }

  _appendProducts(doc) {
    const productGrid = document.querySelector(this.listSelector)
    const products = doc.querySelectorAll(this.cardSelector)

    products.forEach((product) => {
      productGrid.appendChild(product)
    })

    const firstNewProductCard = products[0]
    setTimeout(() => {
      firstNewProductCard.querySelector('a').focus({ preventScroll: true })
    }, 0)

    // Fix for Safari since it has a bug with srcset on dynamically added images
    if (this.isSafari) {
      this._fixSafariSrcsetBug(productGrid)
    }
  }

  _checkLastPage() {
    if (this._currentPage !== this.totalPages) return
    this._isLastPage = true
  }

  async _fetchPage(page) {
    try {
      const url = new URL(window.location.href)
      url.searchParams.set('page', page)
      const response = await fetch(url)
      if (!response.ok) throw new Error(response.statusText)

      const data = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(data, 'text/html')
      return doc
    } catch (error) {
      console.error(error)
    }
  }

  _fixSafariSrcsetBug(grid) {
    const products = [...grid.querySelectorAll(this.cardSelector)].slice(
      this._currentPage * this.perPage,
      this._currentPage * (this.perPage * 2)
    )
    if (products.length === 0) return

    products.forEach((product) => {
      const productImages = product.querySelectorAll('img')
      productImages.forEach((image) => (image.outerHTML = image.outerHTML))
    })
  }

  toggleButtonLoading() {
    if (!this.button) return
    if (this.loading) {
      this.button.setAttribute('disabled', '')
    } else {
      this.button.removeAttribute('disabled')
    }
  }

  get button() {
    return this.querySelector('hh-button-wrapper') ?? null
  }

  render() {
    const progressString = ` ${this.tVerb} ${
      this._isLastPage ? this.totalItems : this.perPage * this._currentPage
    } ${this.tSeparator} ${this.totalItems}`
    const ariaProgressString = `${progressString} ${this.tAria}`

    const progressBar =
      Math.round(((this.perPage * this._currentPage) / this.totalItems) * 100) +
      '%'

    return html`
      <div>
        <p class="progress-string" role="status">
          <span class="visually-hidden">${ariaProgressString}</span>
          <span aria-hidden="true">${progressString}</span>
        </p>
        <span
          class="progress-bar"
          role="progressbar"
          aria-valuenow=${parseInt(progressBar) > 100
            ? '100'
            : progressBar.replace('%', '')}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <span
            class="progress-bar__inner"
            style=${styleMap({ width: progressBar })}
            role="presentation"
          ></span>
        </span>
        <div
          class="${classMap({
            'trigger-wrapper': true,
            'is-hidden': this._isLastPage,
            'is-loading': this.loading,
          })}"
        >
          <slot name="trigger" @click=${this.onLoadMore}></slot>
        </div>
      </div>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin: 4rem 0 10rem;
      }

      .trigger-wrapper {
        display: flex;
        justify-content: center;
        margin-top: 3rem;
      }

      .trigger-wrapper.is-hidden {
        display: none;
      }

      .trigger-wrapper.is-loading {
        pointer-events: none;
      }

      .progress-string {
        font: var(--hh-font-utility);
        text-align: center;
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 0.1rem;
      }

      .progress-bar {
        display: block;
        position: relative;
        width: 20rem;
        height: 0.2rem;
        overflow: hidden;
        background-color: var(--hh-color-brand-tertiary);
        margin-top: 1rem;
      }

      .progress-bar__inner {
        display: block;
        position: absolute;
        inset: 0;
        background-color: var(--hh-color-foreground);
      }
    `,
  ]
}

customElements.define('hh-collection-pagination', CollectionPagination)
